import React from 'react';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import './TableHelper.css';

const TableHelper = ({ columns, data, exportFileName = 'export' }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Export data to CSV
  const handleExportCSV = () => {
    if (!data || data.length === 0) return;
    
    // Extract headers and keys
    const headers = columns.map(col => col.Header).filter(Boolean);
    const keys = columns.map(col => col.accessor).filter(Boolean);
    
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        keys.map(key => {
          const val = row[key];
          const valStr = val === null || val === undefined ? '' : String(val);
          return `"${valStr.replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${exportFileName}_${new Date().toISOString().slice(0,10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="table-helper-container">
      <div className="table-toolbar">
        <div className="table-search">
          <span className="search-icon">⌕</span>
          <input
            value={globalFilter || ''}
            onChange={e => setGlobalFilter(e.target.value || undefined)}
            placeholder="Buscar..."
            className="search-input"
          />
        </div>
        <button onClick={handleExportCSV} className="btn-export">
          📥 Exportar CSV
        </button>
      </div>

      <div className="table-responsive-wrapper">
        <table {...getTableProps()} className="custom-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="custom-table-th"
                  >
                    <div className="th-content">
                      {column.render('Header')}
                      <span className="sort-indicator">
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' 🔽'
                            : ' 🔼'
                          : ' ↕️'}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="custom-table-tr">
                  {row.cells.map(cell => (
                    <td
                      {...cell.getCellProps()}
                      className="custom-table-td"
                      data-label={typeof cell.column.Header === 'string' ? cell.column.Header : ''}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
            {page.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="empty-row">
                  No se encontraron registros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageOptions.length > 0 && (
        <div className="table-pagination">
          <div className="pagination-info">
            Página <strong>{pageIndex + 1} de {pageOptions.length}</strong> ({data.length} registros)
          </div>
          <div className="pagination-controls">
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="btn-page">
              «
            </button>
            <button onClick={() => previousPage()} disabled={!canPreviousPage} className="btn-page">
              ‹
            </button>
            <button onClick={() => nextPage()} disabled={!canNextPage} className="btn-page">
              ›
            </button>
            <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="btn-page">
              »
            </button>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="select-page-size"
            >
              {[10, 25, 50, 100].map(size => (
                <option key={size} value={size}>
                  {size} filas
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableHelper;
